/**
 * Shared logging types for consistent log structures across apps.
 *
 * These types provide a common interface for structured logging,
 * ensuring consistency between console and hosted applications.
 */

/**
 * Log categories for structured logging.
 *
 * Use these categories to classify logs for filtering and analysis.
 *
 * @example
 * logger.info({ category: LogCategory.API }, 'API request started');
 */
export enum LogCategory {
  /** Authentication-related logs */
  AUTH = 'auth',
  /** API request/response logs */
  API = 'api',
  /** UI interaction logs */
  UI = 'ui',
  /** Navigation/routing logs */
  NAVIGATION = 'navigation',
  /** Performance measurement logs */
  PERFORMANCE = 'performance',
  /** Error logs */
  ERROR = 'error',
  /** Analytics event logs */
  ANALYTICS = 'analytics',
}

/**
 * Context object for structured logs.
 *
 * Provides standard fields for observability and debugging.
 *
 * @example
 * const context: LogContext = {
 *   category: LogCategory.API,
 *   correlationId: '123',
 *   traceId: 'abc',
 *   userId: 'user-1',
 * };
 * logger.info(context, 'Request processed');
 */
export interface LogContext {
  /** Log category for filtering */
  category?: LogCategory | string;
  /** Correlation ID for request tracing */
  correlationId?: string;
  /** OpenTelemetry trace ID */
  traceId?: string;
  /** OpenTelemetry span ID */
  spanId?: string;
  /** User ID if authenticated */
  userId?: string;
  /** Workspace ID if applicable */
  workspaceId?: string;
  /** Session ID if applicable */
  sessionId?: string;
  /** Current route/path */
  route?: string;
  /** Component name */
  component?: string;
  /** Allow additional context fields */
  [key: string]: unknown;
}

/**
 * Log levels supported by the logging system.
 */
export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

/**
 * Standard log entry structure for backend submission.
 */
export interface LogEntry {
  /** Log level */
  level: LogLevel;
  /** ISO timestamp */
  timestamp: string;
  /** Log message */
  message: string;
  /** Log category */
  category?: string;
  /** Correlation ID for request tracing */
  correlationId?: string;
  /** OpenTelemetry trace ID */
  traceId?: string;
  /** OpenTelemetry span ID */
  spanId?: string;
  /** Additional context */
  context?: Record<string, unknown>;
}
